import React, { useMemo } from "react";
import { CheckCircle2, Clock, Heart, Play, Timer, CircleDot } from "lucide-react";
import { sessionStatus, answerStatusOf, formatElapsed, nudgeFor } from "../../lib/planner";

export const EntryCard = ({ entry, onEdit, onToggle, onStart, isToday }) => {
  const scriptureRef = useMemo(
    () => [entry.scripture, entry.chapter, entry.verse].filter(Boolean).join(" "),
    [entry.scripture, entry.chapter, entry.verse]
  );

  const sStatus = sessionStatus(entry);
  const aStatus = answerStatusOf(entry);
  const nudge = nudgeFor(entry, isToday);

  return (
    <div
      onClick={() => onEdit(entry)}
      className="group cursor-pointer rounded-lg bg-clay-panel p-3 transition-colors duration-300 hover:bg-clay-soft"
      data-testid={`entry-${entry.id}`}
    >
      <div className="flex items-start justify-between gap-2">
        <p className={`font-sans text-sm leading-snug ${entry.completed ? "text-ink-muted line-through" : "text-ink"}`}>
          {entry.prayer_point || "Untitled prayer"}
        </p>
        <button
          onClick={(e) => onToggle(entry, e)}
          data-testid={`toggle-${entry.id}`}
          aria-label="Toggle completed"
        >
          <CheckCircle2
            className={`h-5 w-5 shrink-0 transition-colors duration-300 ${
              entry.completed ? "text-brand-success" : "text-[#D6D1C6] group-hover:text-ink-muted"
            }`}
          />
        </button>
      </div>

      <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 font-sans text-xs text-ink-muted">
        {scriptureRef && <span>{scriptureRef}</span>}
        {entry.duration_minutes > 0 && (
          <span className="flex items-center gap-1">
            <Clock className="h-3 w-3" /> {entry.duration_minutes}m
          </span>
        )}
        {entry.time_of_prayer && <span>{entry.time_of_prayer}</span>}
      </div>

      {/* Status row */}
      <div className="mt-2 flex flex-wrap items-center gap-1.5">
        {sStatus === "not_started" && (
          <span className="flex items-center gap-1 rounded-full bg-white px-2 py-0.5 text-[11px] text-ink-muted" data-testid={`session-badge-${entry.id}`}>
            <CircleDot className="h-3 w-3" /> Not started
          </span>
        )}
        {sStatus === "in_progress" && (
          <span className="flex items-center gap-1 rounded-full bg-brand/10 px-2 py-0.5 text-[11px] font-medium text-brand" data-testid={`session-badge-${entry.id}`}>
            <Timer className="h-3 w-3" /> In progress · {formatElapsed(entry.started_at)}
          </span>
        )}
        {aStatus === "in_progress" && (
          <span className="rounded-full bg-clay-soft px-2 py-0.5 text-[11px] text-[#7a6a4f]">Praying over</span>
        )}
        {entry.answered && (
          <span className="flex items-center gap-1 rounded-full bg-clay-soft px-2 py-0.5 text-[11px] text-[#9c7f52]" data-testid={`answered-badge-${entry.id}`}>
            <Heart className="h-3 w-3 fill-[#C4A47C] text-[#C4A47C]" /> Answered
          </span>
        )}
      </div>

      {/* Gentle nudge + start action for prayers not yet started */}
      {sStatus === "not_started" && !entry.completed && (
        <div className="mt-2 flex items-center justify-between gap-2 rounded-md bg-white/70 px-2.5 py-2">
          {nudge && <p className="font-serif text-sm italic leading-tight text-ink-soft" data-testid={`nudge-${entry.id}`}>{nudge}</p>}
          <button
            onClick={(e) => { e.stopPropagation(); onStart(entry, e); }}
            className="flex shrink-0 items-center gap-1 rounded-full bg-brand px-2.5 py-1 text-[11px] font-medium text-white transition-colors duration-300 hover:bg-brand-hover"
            data-testid={`start-${entry.id}`}
          >
            <Play className="h-3 w-3" /> Start
          </button>
        </div>
      )}
    </div>
  );
};
